<div class="config-page">

  <!-- LOADING STATE -->
  <div *ngIf="isLoading" class="config-loading">
    <div class="config-loading__spinner">
      <div class="spinner-ring"></div>
    </div>
    <p class="config-loading__text">Fetching configuration details...</p>
  </div>

  <!-- CATEGORY SELECTION -->
  <div *ngIf="!isCategorySelected && !isLoading" class="config-categories animate-fade-in">
    <div class="config-categories__header">
      <h2 class="config-categories__title">Configuration</h2>
      <p class="config-categories__subtitle">Select a category to manage its settings</p>
    </div>

    <div class="row g-4">
      <div class="col-md-4 col-lg-3" *ngFor="let cat of categoryTypeArray">
        <div class="category-card" (click)="onSelectCategory(cat)">
          <div class="category-card__icon">
            <i class="bi bi-sliders2"></i>
          </div>
          <div class="category-card__body">
            <div class="category-card__label">{{ cat.label }}</div>
            <div class="category-card__desc">{{ cat.description }}</div>
          </div>
          <div class="category-card__arrow">
            <i class="bi bi-arrow-right"></i>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- CONFIGURATION FORM -->
  <div *ngIf="isCategorySelected && !isLoading" [formGroup]="configForm" class="config-form animate-slide-up">

    <!-- Sticky Top Bar -->
    <div class="config-topbar sticky-top">
      <div class="config-topbar__left">
        <button class="config-back-btn" (click)="resetSelection()">
          <i class="bi bi-chevron-left"></i>
          <span>Back</span>
        </button>
        <div class="config-topbar__breadcrumb">
          <span class="config-topbar__crumb-parent">Configuration</span>
          <i class="bi bi-chevron-right config-topbar__crumb-sep"></i>
          <span class="config-topbar__crumb-current">{{ selectedCategory?.label }}</span>
        </div>
      </div>

      <div class="config-topbar__right">
        <span class="config-status-label"
          [class.config-status-label--online]="configForm.get('category_active')?.value">
          <span class="config-status-dot"></span>
          {{ configForm.get('category_active')?.value ? 'Online' : 'Offline' }}
        </span>
        <label class="custom-switch scale-up">
          <input type="checkbox" formControlName="category_active">
          <span class="slider"></span>
        </label>
      </div>
    </div>

    <form [formGroup]="configForm">
      <div class="config-sections">

        <div *ngFor="let type of fieldTypes" class="config-section">
          <div class="config-section__header">
            <span class="config-section__accent"></span>
            <h5 class="config-section__title">{{ type }}</h5>
          </div>

          <div class="config-fields">
            <div *ngFor="let field of groupedFields[type]; trackBy: trackByField" class="config-field-row">

              <!-- BOOLEAN -->
              <div *ngIf="field.display_type === 'boolean'" class="config-field config-field--boolean">
                <div class="config-field__toggle-row">
                  <div class="config-field__info">
                    <label class="config-field__label" [for]="field.attribute_code">
                      {{ field.attribute_label }}
                    </label>
                    <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                      [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  </div>
                  <div class="form-check form-switch m-0">
                    <input class="form-check-input config-field__check" type="checkbox"
                      [formControlName]="field.attribute_code" [id]="field.attribute_code">
                  </div>
                </div>
              </div>

              <!-- TEXT -->
              <div *ngIf="field.display_type === 'text' && field.attribute_code !== 'weight_ranges'"
                class="config-field">
                <label class="config-field__label">
                  {{ field.attribute_label }}
                  <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                    [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  <span *ngIf="field.is_required" class="config-field__required">*</span>
                </label>
                <input type="text" class="config-input"
                  [class.config-input--invalid]="isFieldInvalid(field.attribute_code)"
                  [formControlName]="field.attribute_code">
                <div class="config-field__error" *ngIf="configForm.get(field.attribute_code)?.hasError('required')">This
                  field is required.</div>
              </div>

              <!-- TEXTAREA -->
              <div *ngIf="field.display_type === 'textarea'" class="config-field">
                <label class="config-field__label">
                  {{ field.attribute_label }}
                  <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                    [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  <span *ngIf="field.is_required" class="config-field__required">*</span>
                </label>
                <textarea class="config-input config-input--textarea"
                  [class.config-input--invalid]="isFieldInvalid(field.attribute_code)"
                  [formControlName]="field.attribute_code" rows="4"></textarea>
                <div class="config-field__error" *ngIf="configForm.get(field.attribute_code)?.hasError('required')">This
                  field is required.</div>
              </div>

              <!-- NUMBER / DECIMAL -->
              <div *ngIf="field.display_type === 'number' || field.display_type === 'decimal'" class="config-field">
                <label class="config-field__label">
                  {{ field.attribute_label }}
                  <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                    [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  <span *ngIf="field.is_required" class="config-field__required">*</span>
                </label>
                <input type="number" min="0" [step]="field.display_type === 'decimal' ? '0.01' : '1'"
                  class="config-input" [class.config-input--invalid]="isFieldInvalid(field.attribute_code)"
                  [formControlName]="field.attribute_code" placeholder="0">
                <div class="config-field__error" *ngIf="configForm.get(field.attribute_code)?.hasError('required')">This
                  field is required.</div>
                <div class="config-field__error" *ngIf="configForm.get(field.attribute_code)?.hasError('min')">Value
                  cannot be negative.</div>
              </div>

              <!-- DROPDOWN -->
              <div *ngIf="field.display_type === 'dropdown'" class="config-field">
                <label class="config-field__label">
                  {{ field.attribute_label }}
                  <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                    [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  <span *ngIf="field.is_required" class="config-field__required">*</span>
                </label>
                <select class="config-select" [class.config-input--invalid]="isFieldInvalid(field.attribute_code)"
                  [formControlName]="field.attribute_code">
                  <option *ngFor="let opt of field.options" [value]="opt.option_key">{{ opt.option_label }}</option>
                </select>
                <div class="config-field__error" *ngIf="configForm.get(field.attribute_code)?.hasError('required')">
                  Selection is required.</div>
              </div>

              <!-- JSON / OBJECT FIELD -->
              <div
                *ngIf="(field.display_type === 'json' || field.display_type === 'checkbox_text_input' || field.display_type === 'pricing_addon') && field.display_type !== 'weight_range_list' && field.display_type !== 'surcharge_price'"
                [formGroupName]="field.attribute_code" class="config-object-field"
                [class.config-object-field--invalid]="isFieldInvalid(field.attribute_code, true)">
                <div class="config-object-field__header">
                  <span class="config-object-field__title"
                    [class.config-object-field__title--invalid]="isFieldInvalid(field.attribute_code, true)">
                    {{ field.attribute_label }}
                    <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                      [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  </span>
                  <div class="form-check form-switch m-0 d-flex align-items-center gap-2"
                    *ngIf="field.hasEnabledToggle">
                    <input class="form-check-input config-field__check" type="checkbox" formControlName="enabled">
                  </div>
                </div>

                <div class="row g-3 mt-1"
                  *ngIf="!field.hasEnabledToggle || configForm.get(field.attribute_code)?.get('enabled')?.value">
                  <div *ngFor="let key of field.dynamicKeys" [class.col-6]="field.dynamicKeys.length > 1"
                    class="col-12">
                    <label class="config-subfield__label">{{ formatKey(key) }}</label>

                    <select *ngIf="key === 'pricing_method'; else genericObjInput"
                      class="config-select config-select--sm" [formControlName]="key">
                      <option value="fixed">Fixed</option>
                    </select>

                    <ng-template #genericObjInput>
                      <input [type]="isNumber(field, key) ? 'number' : 'text'" [min]="isNumber(field, key) ? 0 : null"
                        [step]="isNumber(field, key) && field.display_type === 'pricing_addon' ? '0.01' : null"
                        class="config-input config-input--sm" [formControlName]="key"
                        [class.config-input--invalid]="configForm.get(field.attribute_code)?.get(key)?.invalid && configForm.get(field.attribute_code)?.get(key)?.touched">
                      <div class="config-field__error"
                        *ngIf="configForm.get(field.attribute_code)?.get(key)?.hasError('min')">Cannot be negative.
                      </div>
                      <div class="config-field__error"
                        *ngIf="configForm.get(field.attribute_code)?.get(key)?.hasError('required')">{{ formatKey(key)
                        }} is required.</div>
                    </ng-template>
                  </div>
                </div>
              </div>

              <!-- WEIGHT & SURCHARGE RANGES -->
              <div *ngIf="field.display_type === 'weight_range_list' || field.display_type === 'surcharge_price'"
                class="config-ranges">
                <div class="config-ranges__header">
                  <div class="config-ranges__title-row">
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <h5 class="config-ranges__title mb-0"
                        [class.config-ranges__title--invalid]="isFieldInvalid(field.attribute_code, true)">
                        {{ field.attribute_label }}
                        <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                          [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                      </h5>
                      <span *ngIf="formSubmitAttempted && getRangeErrorCount(field.attribute_code) > 0"
                        class="range-error-count">
                        <i class="bi bi-exclamation-circle-fill"></i>
                        {{ getRangeErrorCount(field.attribute_code) }} {{ getRangeErrorCount(field.attribute_code) === 1
                        ? 'range' : 'ranges' }} with errors
                      </span>
                    </div>
                    <button type="button" class="config-add-btn" (click)="addWeightRange(field)"
                      *ngIf="getWeightRanges(field.attribute_code).length < 20 && !isReadOnlyUser">
                      <i class="bi bi-plus-lg"></i>
                      Add Range
                    </button>
                  </div>

                  <div class="config-alert config-alert--info">
                    <i class="bi bi-info-circle-fill"></i>
                    Each range must follow the previous by exactly <strong>1</strong> (e.g. 0–50, 51–100) or
                    <strong>0.01</strong> (e.g. 0–50, 50.01–100). Min/Max values accept up to 2 decimal places.
                  </div>
                  <div *ngIf="configForm.get(field.attribute_code)?.hasError('firstRangeZeroError')"
                    class="config-alert config-alert--danger">
                    <i class="bi bi-exclamation-triangle-fill"></i> First range must start at 0.
                  </div>
                  <div *ngIf="configForm.get(field.attribute_code)?.hasError('gapError')"
                    class="config-alert config-alert--danger">
                    <i class="bi bi-exclamation-triangle-fill"></i> Ranges must be continuous — each range must follow
                    the previous by exactly 1 or 0.01 (e.g. 0–50, 51–100 or 0–50, 50.01–100).
                  </div>
                </div>

                <div [formArrayName]="field.attribute_code" class="config-ranges__grid">
                  <div *ngFor="let range of getWeightRanges(field.attribute_code).controls; let i = index"
                    [formGroupName]="i" class="range-card"
                    [class.range-card--invalid]="range.invalid && (range.touched || formSubmitAttempted)">

                    <div class="range-card__header">
                      <div class="d-flex align-items-center gap-2">
                        <span class="range-card__badge">Range {{ i + 1 }}</span>
                        <span *ngIf="range.invalid && (range.touched || formSubmitAttempted)"
                          class="range-card__error-indicator">
                          <i class="bi bi-exclamation-circle-fill"></i> Has errors
                        </span>
                      </div>
                      <button type="button" class="range-card__remove"
                        (click)="removeWeightRange(field.attribute_code, i)"
                        *ngIf="getWeightRanges(field.attribute_code).length > 1 && !isReadOnlyUser"
                        title="Remove range">
                        <i class="bi bi-trash3"></i>
                      </button>
                    </div>

                    <div class="range-card__fields">
                      <!-- Label (weight_range_list only) -->
                      <div class="range-field range-field--full"
                        *ngIf="range.get('label') && field.display_type !== 'surcharge_price'">
                        <label class="range-field__label">Label</label>
                        <input type="text" class="config-input config-input--sm" formControlName="label"
                          [class.config-input--invalid]="range.get('label')?.invalid && (range.get('label')?.touched || formSubmitAttempted)"
                          placeholder="Label">
                      </div>

                      <!-- Min -->
                      <div class="range-field">
                        <label class="range-field__label">Min</label>
                        <input type="number" step="0.01" class="config-input config-input--sm" formControlName="min"
                          [readonly]="i === 0" [class.config-input--readonly]="i === 0"
                          [class.config-input--invalid]="range.get('min')?.invalid && (range.get('min')?.touched || formSubmitAttempted)">
                        <div class="range-field__error"
                          *ngIf="range.get('min')?.hasError('required') && (range.get('min')?.touched || formSubmitAttempted)">
                          Min is required.
                        </div>
                        <div class="range-field__error"
                          *ngIf="range.get('min')?.hasError('tooManyDecimals') && (range.get('min')?.touched || formSubmitAttempted)">
                          Max 2 decimal places.
                        </div>
                      </div>

                      <!-- Max -->
                      <div class="range-field">
                        <label class="range-field__label">Max</label>
                        <input type="number" step="0.01" class="config-input config-input--sm" formControlName="max"
                          [class.config-input--invalid]="range.get('max')?.invalid && (range.get('max')?.touched || formSubmitAttempted)">
                        <div class="range-field__error"
                          *ngIf="range.get('max')?.hasError('required') && (range.get('max')?.touched || formSubmitAttempted)">
                          Max is required.
                        </div>
                        <div class="range-field__error"
                          *ngIf="range.get('max')?.hasError('tooManyDecimals') && (range.get('max')?.touched || formSubmitAttempted)">
                          Max 2 decimal places.
                        </div>
                      </div>

                      <!-- Fee (surcharge_price only) -->
                      <div class="range-field" *ngIf="range.get('fee') && field.display_type === 'surcharge_price'">
                        <label class="range-field__label">Fee <span class="range-field__required">*</span></label>
                        <input type="number" class="config-input config-input--sm" formControlName="fee"
                          [class.config-input--invalid]="range.get('fee')?.invalid && (range.get('fee')?.touched || formSubmitAttempted)"
                          placeholder="0.00">
                        <div class="range-field__error"
                          *ngIf="range.get('fee')?.hasError('required') && (range.get('fee')?.touched || formSubmitAttempted)">
                          Fee is required.
                        </div>
                        <div class="range-field__error"
                          *ngIf="range.get('fee')?.hasError('min') && (range.get('fee')?.touched || formSubmitAttempted)">
                          Fee cannot be negative.
                        </div>
                      </div>

                      <!-- Dynamic keys -->
                      <ng-container *ngFor="let k of field.dynamicRangeKeys">
                        <div class="range-field"
                          *ngIf="k !== 'label' && (k !== 'fee' || field.display_type !== 'surcharge_price')">
                          <label class="range-field__label">{{ formatKey(k) }}</label>
                          <input [type]="k === 'label' ? 'text' : 'number'" class="config-input config-input--sm"
                            [placeholder]="k === 'label' ? '' : '0'" [formControlName]="k"
                            [class.config-input--invalid]="range.get(k)?.invalid && (range.get(k)?.touched || formSubmitAttempted)">
                        </div>
                      </ng-container>
                    </div>

                    <div class="range-card__error"
                      *ngIf="range.hasError('maxNotGreaterThanMin') && (range.touched || formSubmitAttempted)">
                      <i class="bi bi-exclamation-circle"></i> Max must be greater than Min.
                    </div>
                  </div>
                </div>
              </div>

              <!-- ACTION BUTTON -->
              <div *ngIf="field.display_type === 'button'" class="config-field config-field--action">
                <div class="config-field__action-row">
                  <div class="config-field__info">
                    <span class="config-field__label">{{ field.attribute_label }}</span>
                    <i *ngIf="field.help_text" class="bi bi-question-circle config-field__help"
                      [ngbTooltip]="field.help_text" placement="top" container="body"></i>
                  </div>
                  <div class="config-field__action-buttons">
                    <button type="button" class="config-action-btn"
                      [disabled]="field.attribute_code === 'quote_terms_action' && isCategoryTermsLoading"
                      (click)="onActionButtonClick(field)">
                      <span *ngIf="field.attribute_code === 'quote_terms_action' && isCategoryTermsLoading"
                        class="spinner-border spinner-border-sm me-2"></span>
                      <i *ngIf="field.attribute_code !== 'quote_terms_action' || !isCategoryTermsLoading"
                        class="bi bi-file-text me-2"></i>
                      {{ field.attribute_code === 'quote_terms_action' ? 'View Combined Terms' : 'View Terms' }}
                    </button>
                    <ng-container *ngIf="field.attribute_code === 'quote_terms_action'">
                      <button *ngFor="let section of independentCategorySections; trackBy: trackBySectionTitle"
                        type="button" class="config-action-btn config-action-btn--outline"
                        [disabled]="isCategoryTermsLoading"
                        (click)="openIndependentTermModal(section)">
                        {{ section.section_title }}
                      </button>
                    </ng-container>
                  </div>
                </div>
              </div>

              <!-- FLEET (Read Only) -->
              <div *ngIf="field.attribute_code === 'fleet' && field.permission_level === 'read_only'"
                class="config-fleet">
                <div class="config-fleet__header">
                  <h5 class="config-ranges__title">{{ field.attribute_label }}</h5>
                  <span class="config-readonly-badge">Read Only</span>
                </div>
                <div *ngIf="field.value?.length > 0; else noFleet" class="table-responsive">
                  <table class="config-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Rego</th>
                        <th>Brand</th>
                        <th>Capacity</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr *ngFor="let v of field.value">
                        <td>{{ v.name || '—' }}</td>
                        <td>{{ v.rego || v.registration_number || '—' }}</td>
                        <td>{{ v.brand || '—' }}</td>
                        <td>{{ v.capacity || '—' }}</td>
                        <td>{{ v.capacity_type || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <ng-template #noFleet>
                  <p class="text-muted small mt-2">No fleet vehicles configured.</p>
                </ng-template>
              </div>

            </div>
          </div>
        </div>

      </div>
    </form>

    <!-- Sticky Footer -->
    <div class="config-footer sticky-bottom">
      <div class="config-footer__inner">
        <span class="config-footer__label" *ngIf="configForm.invalid">
          <i class="bi bi-exclamation-circle"></i> Please review all required fields
        </span>
        <button class="config-save-btn" [disabled]="isSubmitting || configForm.invalid" (click)="saveConfiguration()">
          <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!isSubmitting" class="bi bi-check2-circle me-2"></i>
          Save {{ selectedCategory?.label }} Settings
        </button>
      </div>
    </div>

  </div>
</div>

<ng-template #quoteTermsModalTemplate let-modal>
  <div class="modal-header border-0 pt-4 px-4">
    <div>
      <h5 class="modal-title fw-bold text-dark mb-1">Quote Terms & Conditions</h5>
      <p class="text-muted small mb-0">General and {{ selectedCategory?.label }} terms as shown to customers.</p>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4 pt-2 quote-terms-view">
    <div *ngIf="isCategoryTermsLoading" class="quote-terms-view__loading">
      <div class="spinner-border text-primary" role="status"></div>
      <p>Loading terms and conditions...</p>
    </div>

    <div *ngIf="!isCategoryTermsLoading && !hasQuoteTermsContent" class="quote-terms-view__empty">
      <i class="bi bi-file-text"></i>
      <p>No terms and conditions have been set yet.</p>
    </div>

    <ng-container *ngIf="!isCategoryTermsLoading && hasQuoteTermsContent">
      <section class="quote-terms-view__group">
        <h6 class="quote-terms-view__group-title">
          <i class="bi bi-globe2"></i>
          General Terms & Conditions
        </h6>
        <div *ngIf="viewGlobalSections.length === 0" class="quote-terms-view__group-empty">
          No general terms have been set.
        </div>
        <article *ngFor="let section of viewGlobalSections" class="quote-terms-view__section">
          <h3 class="quote-terms-view__section-title">{{ section.section_title }}</h3>
          <div class="quote-terms-view__section-content" [innerHTML]="section.section_content"></div>
        </article>
      </section>

      <section class="quote-terms-view__group">
        <h6 class="quote-terms-view__group-title">
          <i class="bi bi-file-text"></i>
          {{ selectedCategory?.label }} Terms & Conditions
        </h6>
        <div *ngIf="viewCategorySections.length === 0" class="quote-terms-view__group-empty">
          No category-specific terms have been set.
        </div>
        <article *ngFor="let section of viewCategorySections" class="quote-terms-view__section">
          <h3 class="quote-terms-view__section-title">{{ section.section_title }}</h3>
          <div class="quote-terms-view__section-content" [innerHTML]="section.section_content"></div>
        </article>
      </section>
    </ng-container>
  </div>
  <div class="modal-footer border-0 p-4 pt-0">
    <button type="button" class="btn btn-light px-4" (click)="modal.dismiss()">Close</button>
  </div>
</ng-template>

<ng-template #independentTermModalTemplate let-modal>
  <div class="modal-header border-0 pt-4 px-4">
    <div>
      <h5 class="modal-title fw-bold text-dark mb-1">{{ selectedIndependentSection?.section_title }}</h5>
      <p class="text-muted small mb-0">Independent term for {{ selectedCategory?.label }}.</p>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body p-4 pt-2 quote-terms-view">
    <article *ngIf="selectedIndependentSection" class="quote-terms-view__section">
      <div class="quote-terms-view__section-content" [innerHTML]="selectedIndependentSection.section_content"></div>
    </article>
  </div>
  <div class="modal-footer border-0 p-4 pt-0">
    <button type="button" class="btn btn-light px-4" (click)="modal.dismiss()">Close</button>
  </div>
</ng-template>